import React from 'react';
import { Link } from 'react-router-dom';
import { HomeHero } from '../components/HomeHero';
import { SectionHeading } from '../components/SectionHeading';
import { MeaningDial } from '../components/MeaningDial';
import { TickDivider } from '../components/TickDivider';
import { ProductCarousel } from '../components/ProductCarousel';
import { WatchFeature } from '../components/WatchFeature';
import { ReadingCard } from '../components/ReadingCard';
import { ClientProof } from '../components/ClientProof';
import { PhotoPlaceholder } from '../components/PhotoPlaceholder';
import { TestimonialSlider } from '../components/TestimonialSlider';
import { VideoCard } from '../components/VideoCard';
import { JournalCard } from '../components/JournalCard';
import { NewsletterBand } from '../components/NewsletterBand';
import { NeedFromAbby } from '../components/NeedFromAbby';
import { Button } from '../components/Button';
import { usePageMeta } from '../hooks/usePageMeta';
import { products } from '../data/products';
import { readings } from '../data/readings';
import { articles, videos } from '../data/journal';
import { images } from '../data/images';
type HomeProps = {
  heroBackground: 'image' | 'dusk';
};
export function Home({
  heroBackground
}: HomeProps) {
  usePageMeta('True Twelve | Vedic Astrology, Ritual & Spiritual Living', 'Modern spiritual living rooted in ancient Vedic wisdom. Shop objects for every hour and book a Vedic astrology reading with Abby.');
  const shopProducts = products.filter(p => p.status === 'available');
  return <>
      <HomeHero background={heroBackground} data-id="element-690" data-mp="287ui59hgvn23" />

      <section aria-labelledby="twelve-title" className="gutter section" data-id="element-691" data-mp="288ngbm2d148m">
        <SectionHeading id="twelve-title" numeral="I" label="The Meaning of Twelve" title="Time is the thread." intro="Twelve hours on the clock. Twelve months in the year. Twelve signs, twelve houses, twelve areas of life. Time is the thread that connects them, and True Twelve is the full circle." data-id="element-692" data-mp="23a2h9pyugqlr" />
        <div className="mt-[clamp(3rem,6vw,7rem)]" data-id="element-693" data-mp="1omc6pjnktqxn">
          <MeaningDial data-id="element-694" data-mp="1on3qzybq428m" />
        </div>
      </section>

      <div className="gutter" data-id="element-695" data-mp="1amszct2lajme">
        <TickDivider data-id="element-696" data-mp="1ankuy3w6shco" />
      </div>

      <section aria-labelledby="collection-title" className="gutter section" data-id="element-697" data-mp="1aonm8cxf8lt4">
        <SectionHeading id="collection-title" numeral="II" label="Shop the Collection" title="Objects for every hour." intro="Jewelry, ritual pieces and altar objects, each designed around the twelve." action={<Button to="/shop" variant="outline" data-id="element-699" data-mp="1tm6wfjvm8ah4">
              Shop All
            </Button>} data-id="element-698" data-mp="1fm5xwb2tpf4t" />
        <div className="mt-10" data-id="element-700" data-mp="1yjgmn1qtwq60">
          <ProductCarousel products={shopProducts} label="Featured products" data-id="element-701" data-mp="1yif8goeinnk2" />
        </div>
      </section>

      <WatchFeature tone="dark" source="home_watch" title={<>
            The True Twelve Watch.
            <br data-id="element-703" data-mp="fl0rztai2yvg" />
            Coming Soon.
          </>} body="Built on the twelve: XII at the top and twelve gold indices, each one a sign, a house and an hour. Join the waitlist for first access." data-id="element-702" data-mp="2m89kj3r0h839" />

      <section aria-labelledby="readings-title" className="section gutter bg-cream" data-id="element-704" data-mp="ocuctrx2avqu">
        <SectionHeading id="readings-title" numeral="III" label="Readings with Abby" title="Know your chart. Know your timing." intro="Abby spends about an hour studying your chart before you meet, then you have a one-hour session together by phone or video." action={<ClientProof index={0} data-id="element-706" data-mp="5ofppfpv81vb" />} data-id="element-705" data-mp="kcaiew3vi68e" />
        <ul className="mt-[clamp(3rem,5vw,6rem)] grid gap-[clamp(1rem,1.6vw,2rem)] lg:grid-cols-3" data-id="element-707" data-mp="ojjd9tp3ycl">
          {readings.map(r => <li key={r.id} data-id="element-708" data-mp="1h1rv3v19wsjl">
              <ReadingCard reading={r} data-id="element-709" data-mp="1h0ykgiz71iw6" />
            </li>)}
        </ul>
        <p className="mt-8 text-graphite" data-id="element-710" data-mp="1c2x2y2hf1rmt">
          Coming back each season?{' '}
          <Link to="/readings#memberships" className="text-charcoal underline underline-offset-4" data-id="element-711" data-mp="27eodnrlw2wkg">
            See memberships &amp; bundles
          </Link>
        </p>
      </section>

      <section aria-labelledby="abby-title" className="grid lg:grid-cols-2" data-id="element-712" data-mp="2hbjdcdtwf30b">
        <PhotoPlaceholder src={images.portrait} alt="Portrait of Abby, Vedic astrologer, seated by a window in morning light" note="brand portrait" className="aspect-[4/5] lg:aspect-auto lg:min-h-[48rem]" data-id="element-713" data-mp="2mb5tav142xct" />
        <div className="gutter flex flex-col justify-center py-[clamp(4rem,8vw,10rem)]" data-id="element-714" data-mp="2vbke7ta5yafs">
          <p className="t-label flex items-center gap-3 text-graphite" data-id="element-715" data-mp="302kaqxfdeqhr">
            <span className="t-numeral text-[1.05em] text-gold-deep" data-id="element-716" data-mp="303c564x8ei6g">IV</span>
            <span aria-hidden="true" className="text-gold" data-id="element-717" data-mp="350c47vvnyko2">
              ·
            </span>
            Meet Abby
          </p>
          <h2 id="abby-title" className="t-h2 mt-5 max-w-[16ch]" data-id="element-718" data-mp="39yneg7pvex6t">
            Sixteen years of reading the sky.
          </h2>
          <p className="t-lead mt-8 max-w-[52ch] text-graphite" data-id="element-719" data-mp="12eqamryl71x3">
            Abby has studied astrology since she was twelve or thirteen. A certified astrologer with a background in Psychology, she
            specializes in Vedic astrology, the ancient system from India, and reads every chart in full before you meet.
          </p>
          <blockquote className="mt-10 max-w-[30ch] border-l border-gold pl-6 font-serif text-[clamp(1.4rem,1.1rem+0.9vw,2.1rem)] italic leading-snug text-charcoal" data-id="element-720" data-mp="ogigsswukgq5">
            “Everybody comes into the world with a different set of energy.”
          </blockquote>
          <dl className="mt-12 grid max-w-[40rem] grid-cols-3 gap-6 border-t border-stone pt-8" data-id="element-721" data-mp="1ccwal40u1xzb">
            {[['16+', 'Years'], ['7', 'Years on birth charts'], ['Vedic', 'Specialist']].map(([v, k]) => <div key={k} data-id="element-722" data-mp="24vjp20lbqerf">
                <dt className="sr-only" data-id="element-723" data-mp="2030df9675tp9">{k}</dt>
                <dd className="text-[clamp(1.5rem,1.2rem+0.9vw,2.4rem)] font-light leading-none text-charcoal" data-id="element-724" data-mp="2015qjpbd7rij">{v}</dd>
                <dd className="mt-2 text-sm text-graphite" data-id="element-725" data-mp="3ucp1jp2exbrh">{k}</dd>
              </div>)}
          </dl>
          <div className="mt-12" data-id="element-726" data-mp="28mfzzlfvzti">
            <Button to="/about" variant="outline" data-id="element-727" data-mp="3klkncrkk9uxp">
              About Abby
            </Button>
          </div>
        </div>
      </section>

      <section aria-labelledby="clients-title" className="gutter section" data-id="element-728" data-mp="3pi1bhlrrg9as">
        <SectionHeading id="clients-title" numeral="V" label="Words from Clients" title="What clients say." data-id="element-729" data-mp="3altqij8j33ug" />
        <div className="mt-[clamp(3rem,5vw,6rem)]" data-id="element-730" data-mp="3fn9digu9nkfh">
          <TestimonialSlider data-id="element-731" data-mp="31m6opwbvzw0v" />
        </div>
      </section>

      <section aria-labelledby="journal-title" className="gutter section bg-cream" data-id="element-732" data-mp="31nrhphijz6wm">
        <SectionHeading id="journal-title" numeral="VI" label="The Journal" title="Notes on time, ritual and the sky." action={<Button to="/journal" variant="outline" data-id="element-734" data-mp="14sloz2zkr6qy">
              Read the Journal
            </Button>} data-id="element-733" data-mp="36ljkx7onefg4" />
        <div className="mt-[clamp(3rem,5vw,6rem)] grid gap-12 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:gap-[clamp(2.5rem,4vw,6rem)]" data-id="element-735" data-mp="1extms4dqan2k">
          <div data-id="element-736" data-mp="1npn8e5846nz0">
            <VideoCard video={videos[0]} featured data-id="element-737" data-mp="1nq5b9kpa80fw" />
            <NeedFromAbby className="mt-4" data-id="element-738" data-mp="1nrzzp8zriaoq">YouTube channel URL</NeedFromAbby>
          </div>
          <ul className="divide-y divide-stone border-y border-stone" data-id="element-739" data-mp="1ixvnaa7q9ag9">
            {articles.slice(0, 3).map(a => <li key={a.slug} className="py-6" data-id="element-740" data-mp="1soyr3js1k3j8">
                <JournalCard article={a} layout="row" data-id="element-741" data-mp="1so6ty4ispxqu" />
              </li>)}
          </ul>
        </div>
      </section>

      <NewsletterBand source="home_letter" data-id="element-742" data-mp="26mnb6peab6n8" />
    </>;
}